import { useCallback } from 'react'
import axios from 'axios'
import { StatusCodes } from 'http-status-codes'
import { useToast } from './useToast'
import { useStore } from '../stores/GlobalStore'

// Module-level lock: concurrent 401s share this flag so only the first
// handler dispatches LOGOUT. LOGOUT unmounts the app tree and re-evaluates
// this module on next login, naturally resetting the flag.
let isLoggingOut = false

export function extractServerMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null
  const data = err.response?.data as { message?: unknown; details?: unknown } | undefined
  if (!data) return null
  if (typeof data.message === 'string' && data.message.length > 0) return data.message
  if (Array.isArray(data.message)) {
    const joined = data.message.filter((item) => typeof item === 'string').join('; ')
    return joined.length > 0 ? joined : null
  }
  return null
}

export function useApiErrorHandler(): (err: unknown) => void {
  const toast = useToast()
  const { dispatch } = useStore()

  const handleError = useCallback((err: unknown) => {
    if (!axios.isAxiosError(err)) {
      console.error(err)
      toast.current?.show({
        severity: 'error',
        summary: 'Unknown Error',
        detail: 'An unexpected error occurred',
        sticky: true,
      })
      return
    }

    const status = err.response?.status

    if (status === undefined) {
      toast.current?.show({
        severity: 'error',
        summary: 'Network Error',
        detail: 'Unable to reach the server, please check your connection',
        sticky: true,
      })
    } else if (status === StatusCodes.UNAUTHORIZED) {
      if (isLoggingOut) return
      isLoggingOut = true
      toast.current?.show({
        severity: 'error',
        summary: 'Session Expired',
        detail: 'Going to logout in 3 seconds... Please login again',
        sticky: true,
      })

      setTimeout(() => {
        dispatch({ type: 'LOGOUT' })
      }, 3000)
    } else if (status === StatusCodes.FORBIDDEN) {
      toast.current?.show({
        severity: 'error',
        summary: 'Forbidden',
        detail: 'You do not have permission to perform this action',
        sticky: true,
      })
    } else if (status === StatusCodes.BAD_REQUEST) {
      toast.current?.show({
        severity: 'error',
        summary: 'Bad Request',
        detail: extractServerMessage(err) ?? 'The request was invalid',
        sticky: true,
      })
    } else if (status === StatusCodes.NOT_FOUND) {
      toast.current?.show({
        severity: 'error',
        summary: 'Not Found',
        detail: 'The requested resource was not found',
        sticky: true,
      })
    } else if (status === StatusCodes.CONFLICT) {
      toast.current?.show({
        severity: 'error',
        summary: 'Conflict',
        detail:
          extractServerMessage(err) ??
          'Data has been modified by another user, please reload and try again',
        sticky: true,
      })
    } else if (status === StatusCodes.UNPROCESSABLE_ENTITY) {
      const serverMessage = extractServerMessage(err)
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Failed',
        detail: serverMessage
          ? `Validation failed: ${serverMessage}`
          : 'Validation failed: please check the submitted values',
        sticky: true,
      })
    } else if (status === StatusCodes.BAD_GATEWAY) {
      toast.current?.show({
        severity: 'error',
        summary: 'Server Gateway Error',
        detail: 'Server gateway error — try again or contact admin',
        sticky: true,
      })
    } else if (status === StatusCodes.INTERNAL_SERVER_ERROR) {
      toast.current?.show({
        severity: 'error',
        summary: 'Internal Server Error',
        detail: 'Please report this issue to the administrator',
        sticky: true,
      })
    } else {
      toast.current?.show({
        severity: 'error',
        summary: `Unexpected Error (HTTP ${status})`,
        detail: 'An unexpected error occurred',
        sticky: true,
      })
    }
  }, [dispatch, toast])

  return handleError
}
